/**
 * What editable pages say before anybody edits them.
 *
 * Each key matches a row in `site_content`. A page renders the row if there
 * is one and this if there is not, so nothing is ever blank because a seed
 * did not run, and an officer starting an edit begins from real text rather
 * than an empty box.
 *
 * These are a starting point, not the final word - the whole purpose of the
 * table is that officers change this without a deploy. Treat what is here as
 * the version that was true when it was written.
 */

export interface DefaultContent {
  title: string
  body: string
}

/** Guild rules, loot rules and the DKP write-up, taken from Discord. */
const GUILD_INFO = `## Welcome to Raven

Raven is a progression guild focused on regular, enjoyable raids for active players.

**We raid Thursdays and Sundays, 21:00–00:00 CET/CEST. Tuesday is an extra night, only when needed.**

This is a team effort — fun and camaraderie matter more than ego. We're here to laugh,
improve, and clear PvE content smoother than last week. If you're into drama, toxicity,
loot obsession or raid-logging, Raven isn't for you.

> *"Try not to become a person of success, but rather try to become a person of value."*
> — Albert Einstein

---

## What we do

- English is the official language in all public channels
- Treat officers and members with respect and civility
- Constructive feedback is welcome — no public insults
- Use food buffs and relevant consumes in raids
- Flasks are optional unless stated before a raid
- All your gear should be enchanted, even if it isn't your BiS item
- Help each other, keep raids fun, and know your boss fights

## What we don't do

- No offensive, hateful, vulgar or discriminatory content
- No offensive character or pet names
- No ninja-looting, scamming or shady auction behaviour
- Violations may result in removal from the guild

**Our reputation depends on your actions.**

**Any member who chooses to raid with other guilds or pugs, resulting in them being saved
and unable to attend our DKP raids, will be subject to a guild kick.**

By applying to Raven, you accept all rules stated above.

---

## Raid voice channel

All raid participants must be in the designated Discord voice channel during Raven raids.
You need to be able to listen to information — speaking is optional.

**If you fail to join the voice channel, you will be removed from the raid.**

---

## Loot rules

### DKP raids

- DKP loot system, master looted
- Items nobody needs go to the guild bank

### Optional raids

These raids do not grant DKP for participation. Loot is distributed as follows.

**Soft reserve (SR).** Each player may SR one item, regardless of main spec or off spec.
You must be max level to be eligible to SR items in optional raids.

**Roll priority:** SR → MS → OS → Transmog

| Roll | Meaning |
|---|---|
| 100 | Main spec |
| 99 | Off spec |
| 98 | Transmog |

**Main spec** is the spec you signed up with on the SR sheet. If an item is an upgrade for
you but is better suited to another spec, you are expected to roll OS for it.

**Alts** have the same loot priority as mains, except for restricted items, which are
distributed via DKP and follow main-only eligibility.

---

## Raven DKP

> "I've heard people say DKP is outdated — and that old means bad. Before we chose our
> system we reviewed every loot method out there. Loot council? Too much stress and drama.
> CEPGP? Encouraged hoarding and sniping. Soft reserve? No memory between raids. Suicide
> Kings? Same flaws.
>
> So I suggested DKP, with a twist. We added a weekly decay to keep things fair and active.
> New players can bid competitively after just one reset, while veterans still have
> priority. Expensive items go for a lot, but some gear drops for the minimum if
> uncontested.
>
> There's no perfect loot system — but Raven DKP is transparent, adaptive and welcoming. It
> honours the players who built this guild and gives new members a real chance.
> **This isn't the old DKP. It's Raven DKP.**"
> — Ferox, co-founder

### Loot priority

- Main spec bids take priority over off spec bids
- Raven does not restrict bidding by class or spec, but MS always takes precedence
- Item priority is listed on the item's page in this app

**No feedback on MS priority loot will be accepted during raids.** If you want an item to
have a different priority, submit a request in Discord.

### Off-spec bids

You bid off spec as your **active OS** — the off spec you registered on your character.
Set it on your profile, and tell an officer if it changes.

### Legendary items

Legendary components are hard-reserved for preassigned receivers and are excluded from DKP
bidding. To apply for legendary loot, post in the Discord request channel.

---

## DKP values

### Participation

| | DKP |
|---|---|
| Raid start, on time and on site | +20 |
| Raid end | +20 |

If a raid leader asks you to join a main raid on an alt for composition reasons, you earn
DKP on your **main**, not the alt you joined on.

### Bonus

Officers may issue a raid DKP bonus once per planned raid. It must apply to every raid
member, not to individuals — for example, on a progression night with no boss kills and
high effort.

### Decay

A guild-wide DKP decay is applied at weekly reset. This encourages bidding and discourages
hoarding. There are no individual penalties — Raven rewards participation.

---

## Bidding

- Every drop gets an auction. Same item twice? One auction, and the top bids win one each.
- Minimum bid and minimum raise are the same step. A new bid extends the timer.
- Need time? Press **Pause** in the bid window.
- **Highest bid when time runs out wins.**

### Conduct

- During bidding, keep Discord activity to a minimum so bids can be handled cleanly
- It is not allowed to influence or interfere with DKP bidding
- It is not allowed to buy an item from another member who received it via DKP
- It is not allowed to give or transfer an item to another member using your own DKP,
  except for transmog items originally obtained for transmog purposes
- It is not allowed to plan, organise or systematically impact the DKP system

---

## Addons

Install the Raven addon suite to see your DKP and bid in raids. Ask an officer for the
current download link — how to use it is documented in the addon itself, under its
settings.
`

export const DEFAULT_CONTENT: Record<string, DefaultContent> = {
  guild_info: {
    title: 'Guild Info',
    body: GUILD_INFO,
  },
  landing_intro: {
    title: '',
    body: `Raven has raided together across several versions of WoW. The roster has held
together through each move, which tells you more about the guild than any progression list
would.

We use DKP so loot is earned and predictable rather than argued about, and we keep our own
site for signups, raid assignments, loot history and attendance.`,
  },
  // The public short version of the loot rules. The full rules are in
  // guild_info, behind the login.
  landing_loot: {
    title: '',
    body: `- **Raven DKP+**, master looted. Show up on time, stay to the end, earn DKP.
- A weekly decay keeps it fair: new members can compete after one reset.
- Every drop is auctioned in game. Main spec beats off spec, and the highest bid wins.
- No class restrictions. Item priorities are on this site.
- Items won with DKP can't be sold or passed on.`,
  },
  home_notice: {
    title: '',
    body: '',
  },
}

export function defaultContent(key: string): DefaultContent {
  return DEFAULT_CONTENT[key] ?? { title: '', body: '' }
}
